/**
 * Puzzly Logger — Structured logging with levels and persistent log history.
 *
 * Logs are stored in-memory (circular buffer of last 500 entries)
 * and can be downloaded as a .log file for debugging.
 *
 * Usage:
 *   import logger from "./services/logger";
 *   logger.info("Puzzle created", { puzzleId: "abc123" });
 *   logger.error("Firestore write failed", { error: err.message });
 *   logger.downloadLog(); // Saves puzzly-log.txt to device
 */

const MAX_LOG_ENTRIES = 500;
const MAX_REMOTE_ERRORS = 20;
const LOG_LEVELS = { DEBUG: 0, INFO: 1, WARN: 2, ERROR: 3 };

class Logger {
  constructor() {
    this.entries = [];
    this.minLevel = LOG_LEVELS.DEBUG;
    this.remoteSink = null;
    this.remoteSent = 0;
  }

  /**
   * Chi riceve gli errori fuori dal telefono (errorReport.js li manda in Cloud
   * Logging). Solo il livello ERROR, al massimo MAX_REMOTE_ERRORS per sessione,
   * e solo messaggio, `data.error` e `data.code`.
   */
  setRemoteSink(fn) {
    this.remoteSink = fn;
  }

  _sendRemote(message, data) {
    if (!this.remoteSink || this.remoteSent >= MAX_REMOTE_ERRORS) return;
    this.remoteSent += 1;
    try {
      const voce = { message, error: data && data.error, code: data && data.code };
      Promise.resolve(this.remoteSink(voce)).catch(() => {});
    } catch {
      // Un sink rotto non deve rompere il log.
    }
  }

  _log(level, message, data = null) {
    const levelName = Object.keys(LOG_LEVELS).find((k) => LOG_LEVELS[k] === level) || "INFO";
    if (level < this.minLevel) return;

    const entry = {
      timestamp: new Date().toISOString(),
      level: levelName,
      message,
      ...(data !== null && data !== undefined ? { data } : {}),
    };

    this.entries.push(entry);

    // Circular buffer — drop oldest entries when limit is reached
    if (this.entries.length > MAX_LOG_ENTRIES) {
      this.entries = this.entries.slice(-MAX_LOG_ENTRIES);
    }

    // Also output to browser console for dev
    const consoleFn = level >= LOG_LEVELS.ERROR ? console.error
      : level >= LOG_LEVELS.WARN ? console.warn
      : level >= LOG_LEVELS.DEBUG ? console.debug
      : console.log;
    const prefix = `[Puzzly ${levelName}]`;
    if (data) {
      consoleFn(prefix, message, data);
    } else {
      consoleFn(prefix, message);
    }

    if (level >= LOG_LEVELS.ERROR) this._sendRemote(message, data);
  }

  debug(message, data) { this._log(LOG_LEVELS.DEBUG, message, data); }
  info(message, data) { this._log(LOG_LEVELS.INFO, message, data); }
  warn(message, data) { this._log(LOG_LEVELS.WARN, message, data); }
  error(message, data) { this._log(LOG_LEVELS.ERROR, message, data); }

  /**
   * Get all log entries as an array.
   */
  getEntries() {
    return [...this.entries];
  }

  /**
   * Format log entries as a readable text string.
   */
  formatLog() {
    return this.entries.map((e) => {
      let line = `${e.timestamp} [${e.level}] ${e.message}`;
      if (e.data) {
        try {
          const dataStr = typeof e.data === "string" ? e.data : JSON.stringify(e.data, null, 2);
          line += `\n  → ${dataStr}`;
        } catch {
          line += `\n  → [unserializable data]`;
        }
      }
      return line;
    }).join("\n");
  }

  /**
   * Download the current log as a text file.
   */
  downloadLog() {
    const content = [
      "═══════════════════════════════════════",
      "  Puzzly — Application Log",
      `  Downloaded: ${new Date().toISOString()}`,
      `  Entries: ${this.entries.length}`,
      `  User Agent: ${navigator.userAgent}`,
      "═══════════════════════════════════════",
      "",
      this.formatLog(),
    ].join("\n");

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `puzzly-log-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Clear all log entries.
   */
  clear() {
    this.entries = [];
  }
}

const logger = new Logger();
export default logger;
